import { z } from "zod";

import {
    TASK_PREFIX,
    FIRST_REVIEWER_TASK_PREFIX,
    SECOND_REVIEWER_TASK_PREFIX,
    MAX_BULK_IMPORT_TASKS,
    VALID_TASK_TYPES,
    TASK_TYPES_REQUIRING_OPTIONS,
    CONDITIONAL_REFERENCE_TASK_TYPES,
    MAX_BULK_IMPORT_REVIEWER_TASKS,
    CONDITIONAL_OPERATOR_MAP,
    CONDITIONAL_ACTION_MAP,
} from "./constants";
import { Task, TaskType } from "./types";

/**
 * Task parsing and validation module.
 * Extracts and validates task data from spreadsheet rows.
 */

export const TaskFieldsSchema = z
    .object({})
    .catchall(z.string())
    .transform((data): { tasks: Task[]; errors: string[] } => {
        return extractTaskColumns(data, TASK_PREFIX, MAX_BULK_IMPORT_TASKS);
    })
    .check((ctx) => {
        if (ctx.value.errors.length > 0) {
            ctx.value.errors.forEach((error) => {
                ctx.issues.push({
                    code: "custom",
                    message: error,
                    input: ctx.value,
                });
            });
        }
    });

export const FirstReviewerTaskFieldsSchema = z
    .object({})
    .catchall(z.string())
    .transform((data): { tasks: Task[]; errors: string[] } => {
        return extractTaskColumns(
            data,
            FIRST_REVIEWER_TASK_PREFIX,
            MAX_BULK_IMPORT_REVIEWER_TASKS
        );
    })
    .check((ctx) => {
        if (ctx.value.errors.length > 0) {
            ctx.value.errors.forEach((error) => {
                ctx.issues.push({
                    code: "custom",
                    message: error,
                    input: ctx.value,
                });
            });
        }
    });

export const SecondReviewerTaskFieldsSchema = z
    .object({})
    .catchall(z.string())
    .transform((data): { tasks: Task[]; errors: string[] } => {
        return extractTaskColumns(
            data,
            SECOND_REVIEWER_TASK_PREFIX,
            MAX_BULK_IMPORT_REVIEWER_TASKS
        );
    })
    .check((ctx) => {
        if (ctx.value.errors.length > 0) {
            ctx.value.errors.forEach((error) => {
                ctx.issues.push({
                    code: "custom",
                    message: error,
                    input: ctx.value,
                });
            });
        }
    });

// Represents a task input before validation
export interface TaskColumnInput {
    title: string | undefined;
    instructions: string | undefined;
    type: string | undefined;
    options: string | undefined;
    required: string | undefined;
    conditionRef: string | undefined;
    conditionIsOrNot: string | undefined;
    conditionValue: string | undefined;
    conditionShowOrHide: string | undefined;
    index: number | undefined;
}

// ----------------
// Helper Functions
// ----------------

/**
 * Type guard to validate if a string is a valid task type
 */
export function isValidTaskType(type: string): type is TaskType {
    return VALID_TASK_TYPES.includes(type as TaskType);
}

/**
 * Extracts a string value from a field or returns undefined if not a string
 */
export function extractStringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value.trim() : undefined;
}

/**
 * Extracts task data from a specific index in the spreadsheet row
 */
export function extractTaskAtIndex(
    row: Record<string, unknown>,
    index: number,
    prefix: string
): TaskColumnInput {
    return {
        title: extractStringValue(row[`${prefix}${index}Title`]),
        instructions: extractStringValue(row[`${prefix}${index}Instructions`]),
        type: extractStringValue(row[`${prefix}${index}Type`]),
        options: extractStringValue(row[`${prefix}${index}Options`]),
        required: extractStringValue(row[`${prefix}${index}Required`]),
        conditionRef: extractStringValue(row[`${prefix}${index}Condition_ref`]),
        conditionIsOrNot: extractStringValue(
            row[`${prefix}${index}Condition_is_or_not`]
        ),
        conditionValue: extractStringValue(
            row[`${prefix}${index}Condition_value`]
        ),
        conditionShowOrHide: extractStringValue(
            row[`${prefix}${index}Condition_show_or_hide`]
        ),
        index,
    };
}

/**
 * Parses and validates conditional fields for a task
 * @param prefix - The prefix to use for references (e.g., "Task", "FirstReviewerTask")
 * @returns Either the parsed condition object or an error message
 */
export function parseConditionalFields(
    input: TaskColumnInput,
    allTaskInputs: TaskColumnInput[],
    prefix: string
):
    | { condition: Task["condition"]; error: null }
    | { condition: undefined; error: string } {
    // Skip parsing if no conditional fields are provided
    if (
        !input.conditionRef &&
        !input.conditionIsOrNot &&
        !input.conditionValue &&
        !input.conditionShowOrHide
    ) {
        return { condition: undefined, error: null };
    }

    // All conditional fields are required if any are provided
    if (
        !input.conditionRef ||
        !input.conditionIsOrNot ||
        !input.conditionValue ||
        !input.conditionShowOrHide
    ) {
        return {
            condition: undefined,
            error: `If any conditional field is provided, all conditional fields (Condition_ref, Condition_is_or_not, Condition_value, Condition_show_or_hide) are required for ${prefix}${input.index}`,
        };
    }

    // Check for proper ref format using the provided prefix and extract the index
    const refTaskPattern = new RegExp(`^${prefix}(\\d+)$`);
    const refTaskMatch = refTaskPattern.exec(input.conditionRef);
    if (!refTaskMatch) {
        return {
            condition: undefined,
            error: `Condition_ref must be in the format "${prefix}<number>", got "${input.conditionRef}"`,
        };
    }

    const refTaskIndex = parseInt(refTaskMatch[1], 10);

    if (refTaskIndex >= (input.index || 0)) {
        return {
            condition: undefined,
            error: `Condition_ref must reference a task that appears before ${prefix}${input.index}, got "${input.conditionRef}"`,
        };
    }

    const refTask = allTaskInputs.find((task) => task.index === refTaskIndex);
    if (!refTask || !refTask.type) {
        return {
            condition: undefined,
            error: `Condition_ref references ${prefix}${refTaskIndex} which is not defined or doesn't have a type`,
        };
    }

    if (!CONDITIONAL_REFERENCE_TASK_TYPES.includes(refTask.type as TaskType)) {
        return {
            condition: undefined,
            error: `Condition_ref can only reference tasks of type ${CONDITIONAL_REFERENCE_TASK_TYPES.join(
                ", "
            )}, got "${refTask.type}"`,
        };
    }

    if (input.conditionIsOrNot !== "is" && input.conditionIsOrNot !== "not") {
        return {
            condition: undefined,
            error: `Condition_is_or_not must be one of: "is", "not". Got "${input.conditionIsOrNot}"`,
        };
    }

    if (
        input.conditionShowOrHide !== "show" &&
        input.conditionShowOrHide !== "hide"
    ) {
        return {
            condition: undefined,
            error: `Condition_show_or_hide must be one of: "show", "hide". Got "${input.conditionShowOrHide}"`,
        };
    }

    const refTaskOptions = refTask.options
        ? refTask.options
              .split(";")
              .map((o) => o.trim())
              .filter(Boolean)
        : [];
    if (!refTaskOptions.includes(input.conditionValue)) {
        return {
            condition: undefined,
            error: `Condition_value "${
                input.conditionValue
            }" must be one of the options of ${prefix}${refTaskIndex}: [${refTaskOptions.join(
                ", "
            )}]`,
        };
    }

    const operator = CONDITIONAL_OPERATOR_MAP[input.conditionIsOrNot];
    const action = CONDITIONAL_ACTION_MAP[input.conditionShowOrHide];

    return {
        condition: {
            refTaskIndex,
            operator,
            value: input.conditionValue,
            action,
        },
        error: null,
    };
}

/**
 * Parses a single task input into a Task object
 * @param input - The task input to parse
 * @param allTaskInputs - All task inputs for reference
 * @param prefix - Prefix for the task (e.g., "Task", "FirstReviewerTask", "SecondReviewerTask")
 */
export function parseTaskInput(
    input: TaskColumnInput,
    allTaskInputs: TaskColumnInput[],
    prefix = TASK_PREFIX
): Task | { error: string } | null {
    if (!input.title?.trim()) {
        return null;
    }

    if (!input.type || !isValidTaskType(input.type)) {
        return {
            error: `Invalid task type: "${
                input.type
            }". Valid types are: ${Array.from(VALID_TASK_TYPES).join(", ")}`,
        };
    }

    const options = String(input.options || "")
        .split(";")
        .map((o) => o.trim())
        .filter(Boolean);

    if (
        TASK_TYPES_REQUIRING_OPTIONS.includes(input.type) &&
        options.length === 0
    ) {
        return {
            error: `Options are required for task type "${input.type}"`,
        };
    }

    if (input.required && !["Y", "N"].includes(input.required.toUpperCase())) {
        return {
            error: `Required field must be one of "Y" or "N", got "${input.required}"`,
        };
    }

    const task: Task = {
        title: input.title,
        instructions: String(input.instructions || ""),
        type: input.type,
        options,
        required: input.required?.toUpperCase() === "Y",
    };

    const conditionalResult = parseConditionalFields(
        input,
        allTaskInputs,
        prefix
    );
    if (conditionalResult.error) {
        return { error: conditionalResult.error };
    }

    if (conditionalResult.condition !== undefined) {
        task.condition = conditionalResult.condition;
    }

    return task;
}

/**
 * Extracts and validates all tasks from a spreadsheet row
 */
export function extractTaskColumns(
    row: Record<string, unknown>,
    prefix: string,
    maxTasks: number
): { tasks: Task[]; errors: string[] } {
    const errors: string[] = [];

    const taskKeyPattern = new RegExp(`^${prefix}(\\d+)`);
    const taskIndices = Object.keys(row)
        .map((key) => {
            const match = taskKeyPattern.exec(key);
            return match ? parseInt(match[1], 10) : 0;
        })
        .filter((index) => index > 0);

    const maxTaskIndex = Math.max(...taskIndices, 0);
    if (maxTaskIndex > maxTasks) {
        errors.push(`Cannot have more than ${maxTasks} ${prefix}s`);
    }

    const allTaskInputs = [...new Set(taskIndices)]
        .map((index) => extractTaskAtIndex(row, index, prefix))
        .filter((input) =>
            Boolean(
                input.title ||
                input.instructions ||
                input.type ||
                input.options ||
                input.required ||
                input.conditionRef ||
                input.conditionIsOrNot ||
                input.conditionValue ||
                input.conditionShowOrHide
            )
        );

    const tasks = allTaskInputs
        .map((taskInput) => {
            if (!taskInput.title) {
                errors.push(
                    `${prefix}${taskInput.index}Title - ${prefix}${taskInput.index}Title is required when task ${taskInput.index} is present`
                );
            }

            if (!taskInput.type) {
                errors.push(
                    `${prefix}${taskInput.index}Type - ${prefix}${taskInput.index}Type is required when task ${taskInput.index} is present`
                );
            }

            const result = parseTaskInput(taskInput, allTaskInputs, prefix);

            if (result && "error" in result) {
                errors.push(`${prefix}${taskInput.index}: ${result.error}`);
                return null;
            }
            return result;
        })
        .filter((task): task is Task => task !== null);

    return { tasks, errors };
}
